"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.gradeCheck = gradeCheck;
exports.gradeStep = gradeStep;
exports.gradeRun = gradeRun;
const pure_1 = require("./pure");
const asRecord = (value) => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const asNumber = (value) => {
    if (typeof value === 'number' && Number.isFinite(value))
        return value;
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
        return Number(value);
    }
    return null;
};
const asStringArray = (value) => Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
function fail(check, wrong, message) {
    return { checkId: check.id, correct: false, earned: 0, possible: check.points, wrong, message };
}
function pass(check) {
    return { checkId: check.id, correct: true, earned: check.points, possible: check.points, wrong: [] };
}
/**
 * Grades one check against one response.
 *
 * Response shapes, by check type:
 *
 *   tally-match   `{ [categoryKey]: number }`
 *   table-match   `{ rows: { [categoryKey]: { frequency: number, tally?: number } }, total?: number }`
 *   graph-match   `{ kind, scale, symbolValue?, values: { [categoryKey]: number } }`
 *   mcq           `string[]` of option ids
 *   numeric       `number`
 *   ordering      `string[]` of item ids, in the learner's order
 *
 * A response of the wrong shape is wrong, not an exception. A lab that throws
 * on a malformed answer is a lab that loses a learner's whole session to one
 * bad drag.
 */
function gradeCheck(check, response, datasets) {
    switch (check.type) {
        case 'mcq': {
            const chosen = new Set(asStringArray(response));
            const correct = new Set(check.correct);
            if (chosen.size !== correct.size) {
                return fail(check, [...chosen].filter(id => !correct.has(id)), check.explanation);
            }
            const wrong = [...chosen].filter(id => !correct.has(id));
            return wrong.length ? fail(check, wrong, check.explanation) : pass(check);
        }
        case 'numeric': {
            const value = asNumber(response);
            if (value === null)
                return fail(check, ['value'], check.explanation);
            const within = Math.abs(value - check.answer) <= check.tolerance;
            return within ? pass(check) : fail(check, ['value'], check.explanation);
        }
        case 'ordering': {
            const given = asStringArray(response);
            if (given.length !== check.correct.length)
                return fail(check, ['order']);
            const wrong = check.correct.flatMap((id, index) => (given[index] === id ? [] : [id]));
            return wrong.length ? fail(check, wrong) : pass(check);
        }
        case 'tally-match': {
            const dataset = datasets.get(check.datasetId);
            if (!dataset)
                return fail(check, ['dataset'], `unknown dataset ${check.datasetId}`);
            const expected = (0, pure_1.datasetCounts)(dataset);
            const keys = check.categories ?? Object.keys(expected);
            const given = asRecord(response);
            const wrong = keys.filter(key => asNumber(given[key]) !== expected[key]);
            return wrong.length ? fail(check, wrong) : pass(check);
        }
        case 'table-match': {
            const dataset = datasets.get(check.datasetId);
            if (!dataset)
                return fail(check, ['dataset'], `unknown dataset ${check.datasetId}`);
            const body = asRecord(response);
            const rows = asRecord(body.rows);
            const wrong = [];
            for (const category of dataset.categories) {
                const row = asRecord(rows[category.key]);
                if (asNumber(row.frequency) !== category.count) {
                    wrong.push(category.key);
                    continue;
                }
                if (check.requireTally) {
                    // The tally column is checked as a count of marks, not as a string of
                    // glyphs. Whether a learner drew five singles or a gate of five is a
                    // rendering question, and marking on it would fail the text tier for
                    // a reason that has nothing to do with data handling.
                    const { fives, remainder } = (0, pure_1.tallyGroups)(category.count);
                    const marks = asNumber(row.tally);
                    if (marks !== fives * 5 + remainder)
                        wrong.push(`${category.key}.tally`);
                }
            }
            if (check.requireTotal && asNumber(body.total) !== (0, pure_1.datasetTotal)(dataset)) {
                wrong.push('total');
            }
            return wrong.length ? fail(check, wrong) : pass(check);
        }
        case 'graph-match': {
            const dataset = datasets.get(check.datasetId);
            if (!dataset)
                return fail(check, ['dataset'], `unknown dataset ${check.datasetId}`);
            const body = asRecord(response);
            const wrong = [];
            if (body.kind !== check.kind)
                wrong.push('kind');
            // "Determine a suitable scale" is the objective, so the scale is part of
            // the answer. `acceptedScales` is a list because 2 and 5 can both be
            // suitable for the same data and marking one wrong teaches nothing.
            const scale = asNumber(body.scale);
            if (scale === null || !check.acceptedScales.includes(scale))
                wrong.push('scale');
            if (check.kind === 'pictograph' && check.symbolValue !== undefined) {
                if (asNumber(body.symbolValue) !== check.symbolValue)
                    wrong.push('symbolValue');
            }
            const values = asRecord(body.values);
            for (const category of dataset.categories) {
                const plotted = asNumber(values[category.key]);
                if (plotted === null || Math.abs(plotted - category.count) > check.tolerance) {
                    wrong.push(category.key);
                }
            }
            return wrong.length ? fail(check, wrong) : pass(check);
        }
    }
}
function gradeStep(script, stepId, responses) {
    const step = script.steps.find(candidate => candidate.id === stepId);
    if (!step) {
        return { stepId, results: [], earned: 0, possible: 0, passed: false };
    }
    const datasets = new Map(script.datasets.map(dataset => [dataset.id, dataset]));
    const byId = new Map(responses.map(response => [response.checkId, response.value]));
    const results = step.checks.map(check => gradeCheck(check, byId.get(check.id), datasets));
    // Formative checks are marked so the learner sees a tick, but they do not
    // count. Step 1 is exploration — penalising a first look at the data would be
    // marking curiosity.
    const counted = step.checks.filter(check => !check.formative).map(check => check.id);
    const earned = results
        .filter(result => counted.includes(result.checkId))
        .reduce((sum, result) => sum + result.earned, 0);
    const possible = results
        .filter(result => counted.includes(result.checkId))
        .reduce((sum, result) => sum + result.possible, 0);
    const passed = possible === 0 ? true : earned / possible >= (step.scoring.passFraction || 0) - 1e-9;
    return { stepId, results, earned, possible, passed };
}
/** Convenience for the server, which holds the script and a whole run's answers. */
function gradeRun(script, answers) {
    const steps = script.steps.map(step => gradeStep(script, step.id, answers[step.id] ?? []));
    return {
        steps,
        earned: steps.reduce((sum, step) => sum + step.earned, 0),
        possible: steps.reduce((sum, step) => sum + step.possible, 0),
    };
}
//# sourceMappingURL=grading.js.map